import { EventEmitter, Inject } from '@angular/core';

import { Severity } from 'core/logging/constants';
import { LoggingService } from 'core/logging/logging.service';
import { TranslationService } from 'core/translation/translation.service';
import { Utils } from 'core/utils';
import { CriticalError, ErrorBase, TrivialError } from './error';

export class HttpOrSyntaxError extends CriticalError
{
  protected _name: string = 'HttpOrSyntaxError';
}

export class ErrorService
{
  public criticalErrorOccurred: EventEmitter<CriticalError> =
      new EventEmitter();
  public errorsOccurred: EventEmitter<Array<ErrorBase>> = new EventEmitter();

  private errors: {[origin: string]: {[type: string]: Array<ErrorBase> }} = {};

  constructor(
      @Inject(LoggingService) private loggingService: LoggingService,
      @Inject(TranslationService) private translationService)
  {}

  public add(error: ErrorBase,
             origin: string = 'default',
             shouldLogError: boolean = true): void
  {
    if (shouldLogError) {
      this.logError(error, origin);
    }

    if (error instanceof CriticalError) {
      this.criticalErrorOccurred.emit(error);
    }
    else {
      if (!this.errors.hasOwnProperty(origin)) {
        this.errors[origin] = {};
      }
      if (!this.errors[origin].hasOwnProperty(error.name)) {
        this.errors[origin][error.name] = [];
      }

      const translateMessage = (reference, message, name) => {
        const languageKey = this.translationService.getLanguageKeyPrefix(
            'error', name);
        return this.translationService.translateErrorMessage(
            languageKey, reference) || message;
      };

      if (this.errors[origin][error.name].filter(e => {
          return translateMessage(e.reference, e.message, e.name) ===
              translateMessage(error.reference, error.message, error.name);
          }).length < 1) {
        this.errors[origin][error.name].push(error);
      }
    }
  }

  public clear(): void
  {
    this.errors = {};
    this.criticalErrorOccurred.emit(undefined);
  }

  public getErrors(): any
  {
    return this.errors;
  }

  public flushByType(origin: string = 'default'): void
  {
    if (this.errors[origin]) {
      for (const type in this.errors[origin]) {
        if (type in this.errors[origin]) {
          this.errorsOccurred.emit(this.errors[origin][type]);
        }
      }
      delete this.errors[origin];
    }
  }

  /**
   * Methode wird von Komponenten / Services aufgerufen, die auch die
   * add Methode des ErrorService verwenden
   */
  public flush(origin: string = 'default'): void
  {
    if (this.errors[origin]) {
      const occurred = Utils.objectValuesToArray(this.errors[origin])
        .reduce((acc, val) => acc.concat(val), []);
      this.errorsOccurred.emit(occurred);
      delete this.errors[origin];
    }
  }

  /**
   * Methode kann jederzeit aufgerufen werden
   */
  public flushAll(): void
  {
    let errors: Array<ErrorBase> = [];
    for (let origin in this.errors) {
      if (this.errors[origin]) {
        errors.concat(Utils.objectValuesToArray(this.errors[origin])
          .reduce((acc, val) => acc.concat(val), []));
        delete this.errors[origin];
      }
    }
    this.errorsOccurred.emit(errors);
  }

  private logError(error: ErrorBase, origin: string): void
  {
    let severity: string;
    if (error instanceof TrivialError) {
      severity = Severity.WARNING;
    }
    else if (error instanceof CriticalError) {
      severity = Severity.ERROR;
    }
    else {
      this.logError(
        new TrivialError('received unkown error: ' + error),
        origin);
      severity = Severity.ERROR;
    }

    const reference = error.reference || [];
    const message = `${error.message} / ${reference.join(', ')}`;
    this.loggingService.log(origin, severity, message);
  }

  /**
   * @throws HttpOrSyntaxError
   */
  public catchHttpOrSyntaxError(error: Error | Response, origin: string): void
  {
    if (error.hasOwnProperty('status') &&
        error.hasOwnProperty('statusText') &&
        error.hasOwnProperty('url')
    ) {
      error = <Response> error;
      error = new HttpOrSyntaxError(
          '[' + error.status + '] "' +
          error.url + '" ' + error.statusText + ' (' + origin + ')');
    }
    else {
      error = <Error> error;
      error = new HttpOrSyntaxError(error.message + ' (' + origin + ')');
    }

    this.add(<ErrorBase> error, origin);
    this.flush(origin);
    throw error;
  }
}



// WEBPACK FOOTER //
// ./src/app/core/error/error.service.ts